import { useEffect, useRef, useState } from 'react'
import { Avatar, cn } from '@ludus/ui'
import { Filter } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { managerDisplayName, managerInitials, type ManagerId } from '@ludus/core'
import { usePipelineManagers } from '../../data/usePipelinesSource.ts'

interface FilterByManagerProps {
  selected: ManagerId[]
  onToggle: (id: ManagerId) => void
  onReset: () => void
}

export function FilterByManager({ selected, onToggle, onReset }: FilterByManagerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const managers = usePipelineManagers()

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const label =
    selected.length === 0
      ? t('pages.pipelines.filters.managerAny', { defaultValue: 'Any owner' })
      : t('pages.pipelines.filters.managerN', {
          defaultValue: '{{count}} owner(s)',
          count: selected.length,
        })

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex h-9 items-center gap-2 rounded-md border border-border bg-input px-3 text-sm text-foreground',
          'outline-none transition-colors hover:border-border-hover',
          'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40',
        )}
      >
        <Filter className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
        <span className="text-foreground">{label}</span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute right-0 z-30 mt-1 min-w-[220px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-border bg-background-app py-1 shadow-lg"
        >
          {managers.map((m) => {
            const isSelected = selected.includes(m.id)
            return (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onToggle(m.id)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                  isSelected
                    ? 'bg-primary-light text-foreground'
                    : 'text-foreground hover:bg-sidebar-hover-bg',
                )}
              >
                <Avatar
                  size="xs"
                  initials={managerInitials(m)}
                  seed={m.id}
                  aria-label={managerDisplayName(m)}
                />
                <span className="truncate">{managerDisplayName(m)}</span>
                {isSelected && <span className="ml-auto text-xs text-primary">✓</span>}
              </button>
            )
          })}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="block w-full border-t border-border/40 px-3 py-1.5 text-left text-xs text-muted hover:bg-sidebar-hover-bg hover:text-foreground"
            >
              {t('pages.pipelines.filters.clearOwners', { defaultValue: 'Clear owners' })}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
